import React from "react";
import { motion } from "framer-motion";
import {
  Home, Building2, Car, Landmark, Users2, ShieldAlert, Check, AlertCircle, Info,
} from "lucide-react";
import Skeleton from "@/components/jea/Skeleton";
import { BROKER_META } from "@/lib/queries";
import { centsToUSD, shortDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

const SectionHeader = ({ children, testid }) => (
  <div className="flex items-center gap-2 mt-4 mb-2" data-testid={testid}>
    <div className="text-accent-pink font-semibold text-[11px] tracking-[0.08em] uppercase">
      {children}
    </div>
    <div className="flex-1 h-px bg-accent-pink/15" />
  </div>
);

const KV = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-3 text-[12px]">
    <span className="text-text-secondary">{label}</span>
    <span className={cn("text-white text-right max-w-[70%]", mono && "jea-mono")}>{value ?? "—"}</span>
  </div>
);

const SeverityIcon = ({ severity }) => {
  if (severity === "danger") return <AlertCircle size={14} className="text-danger" />;
  if (severity === "warning") return <AlertCircle size={14} className="text-warning" />;
  return <Info size={14} className="text-text-secondary" />;
};

const alertStyle = (severity) => {
  if (severity === "danger") return "border-l-2 border-danger bg-danger/10 text-danger";
  if (severity === "warning") return "border-l-2 border-warning bg-warning/10 text-warning";
  return "border-l-2 border-accent-pink/40 bg-accent-pink/5 text-white";
};

const AlertRow = ({ a }) => (
  <div className={cn("rounded-[6px] px-3 py-2 flex items-start gap-2 text-[12px]", alertStyle(a.severity))}>
    <SeverityIcon severity={a.severity} />
    <div className="flex-1">{a.message}</div>
  </div>
);

/**
 * BrokerResultCard — renders a single broker's payload.
 * When `pending` is true, shows a skeleton state (pink shimmer roughly matching the filled shape).
 */
const BrokerResultCard = ({ pending, result, index }) => {
  const slug = result?.broker_slug;
  const meta = slug ? BROKER_META[slug] : null;
  const Icon = meta?.Icon;
  const status = result?.status || (pending ? "pending" : "ok");

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: (index || 0) * 0.05, ease: [0.4, 0, 0.2, 1] }}
      className={cn(
        "bg-bg-secondary border rounded-[12px] p-5",
        status === "error" && "border-danger/50",
        status === "no_match" && "border-warning/40",
        status === "ok" && "border-border-default",
        pending && "border-accent-pink/30"
      )}
      data-testid={`broker-card-${slug || `pending-${index}`}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {pending ? (
            <div className="w-9 h-9 rounded-[10px] bg-accent-pink/15 border border-accent-pink/40 flex items-center justify-center animate-pulse">
              <div className="w-3 h-3 rounded-full bg-accent-pink" />
            </div>
          ) : Icon ? (
            <div className={cn(
              "w-9 h-9 rounded-[10px] border flex items-center justify-center shrink-0",
              status === "error" ? "bg-danger/10 border-danger/40" :
              status === "no_match" ? "bg-warning/10 border-warning/40" :
              "bg-accent-pink/10 border-accent-pink/40"
            )}>
              <Icon size={16} className={
                status === "error" ? "text-danger" :
                status === "no_match" ? "text-warning" :
                "text-accent-pink"
              } />
            </div>
          ) : null}
          <div className="min-w-0">
            <div className="text-white font-semibold text-[14px] leading-tight truncate">
              {pending ? <Skeleton className="h-4 w-32" /> : result?.broker_name}
            </div>
            <div className="text-text-secondary text-[12px] mt-0.5">
              {pending ? <Skeleton className="h-3 w-24 mt-1" /> : (meta?.tagline || result?.category)}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {!pending && (
            <span
              className="inline-flex px-2 py-[2px] rounded-[4px] text-[12px] font-semibold border"
              style={{
                borderColor: "var(--color-accent)",
                color: "var(--color-accent)",
              }}
              data-testid="broker-demo-data-badge"
            >
              Demo Data
            </span>
          )}
          {pending ? (
            <Skeleton className="h-3 w-16" />
          ) : (
            <>
              <div className="jea-mono text-accent-pink text-[13px] font-bold">
                {centsToUSD(result?.price_cents || 0)}
              </div>
              <div className="jea-mono text-text-muted text-[10px]">
                {result?.latency_ms ?? 0}ms
              </div>
            </>
          )}
        </div>
      </div>

      {/* Body */}
      {pending ? (
        <div className="mt-4 space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
          <Skeleton className="h-3 w-3/5" />
          <Skeleton className="h-16 w-full mt-4" />
        </div>
      ) : status === "error" ? (
        <div className="mt-3 text-[13px] text-danger">{result?.raw_summary}</div>
      ) : (
        <>
          {result?.raw_summary && (
            <p className="mt-3 text-text-secondary text-[12px] leading-5">{result.raw_summary}</p>
          )}

          {/* Identity (TLOxp) */}
          {result?.identity && (
            <>
              <SectionHeader>Identity</SectionHeader>
              <div className="space-y-1.5">
                <KV
                  label="Verified"
                  value={result.identity.verified ? (
                    <span className="inline-flex items-center gap-1 text-success">
                      <Check size={12} /> Confirmed
                    </span>
                  ) : "No match"}
                />
                <KV label="SSN Confirmed" value={result.identity.ssn_confirmed ? "Yes" : "No"} />
                <KV label="Aliases" value={(result.identity.aliases || []).join(", ") || "None"} />
                <KV label="Known Associates" value={result.identity.known_associates_count} mono />
              </div>
            </>
          )}

          {/* Addresses */}
          {(result?.addresses?.length || 0) > 0 && (
            <>
              <SectionHeader>Addresses</SectionHeader>
              <ul className="space-y-1.5 text-[12px]">
                {result.addresses.slice(0, 4).map((a, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Home size={12} className={a.current ? "text-accent-pink mt-0.5 shrink-0" : "text-text-muted mt-0.5 shrink-0"} />
                    <div>
                      <span className="text-white">{a.street}, {a.city}, {a.state} {a.zip}</span>
                      {a.current && <span className="ml-2 text-[10px] text-accent-pink font-bold tracking-[0.08em]">CURRENT</span>}
                      <div className="text-text-muted jea-mono text-[10px]">
                        seen {shortDate(a.first_seen)} → {shortDate(a.last_seen)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Employers */}
          {(result?.employers?.length || 0) > 0 && (
            <>
              <SectionHeader>Employer{result.employers.length > 1 ? "s" : ""}</SectionHeader>
              <ul className="space-y-2 text-[12px]">
                {result.employers.slice(0, 3).map((e, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Users2 size={12} className="text-text-muted mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="text-white font-semibold flex items-center gap-2">
                        {e.name}
                        {e.verified && (
                          <span className="inline-flex items-center gap-1 text-success text-[10px] font-bold tracking-[0.08em]">
                            <Check size={10} /> VERIFIED
                          </span>
                        )}
                      </div>
                      <div className="text-text-secondary">{e.position || "—"} · {e.employment_type}</div>
                      {e.income_annual_cents > 0 && (
                        <div className="jea-mono text-accent-pink font-bold mt-0.5">
                          {centsToUSD(e.income_annual_cents)}/yr · {e.pay_frequency}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Real Property */}
          {(result?.real_property?.length || 0) > 0 && (
            <>
              <SectionHeader>Real Property</SectionHeader>
              <ul className="space-y-2 text-[12px]">
                {result.real_property.map((p, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Building2 size={12} className="text-text-muted mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="text-white">{p.address}</div>
                      <div className="text-text-secondary">
                        {p.county} · APN <span className="jea-mono">{p.apn}</span>
                        {p.encumbered && p.lien_holder && <span> · Lien: {p.lien_holder}</span>}
                      </div>
                    </div>
                    <span className="jea-mono text-accent-pink font-bold whitespace-nowrap">
                      {centsToUSD(p.estimated_value_cents)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Vehicles */}
          {(result?.vehicles?.length || 0) > 0 && (
            <>
              <SectionHeader>Vehicles</SectionHeader>
              <ul className="space-y-2 text-[12px]">
                {result.vehicles.map((v, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Car size={12} className="text-text-muted mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="text-white">{v.year} {v.make} {v.model}</div>
                      <div className="text-text-secondary">
                        VIN <span className="jea-mono">{v.vin}</span> · Reg {v.registration_state}
                        {v.lien_holder && <span> · Lien: {v.lien_holder}</span>}
                      </div>
                    </div>
                    <span className="jea-mono text-accent-pink font-bold whitespace-nowrap">
                      {centsToUSD(v.estimated_value_cents)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Business Associations */}
          {(result?.business_associations?.length || 0) > 0 && (
            <>
              <SectionHeader>Business Associations</SectionHeader>
              <ul className="space-y-1.5 text-[12px]">
                {result.business_associations.slice(0, 3).map((b, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Landmark size={12} className="text-text-muted mt-0.5 shrink-0" />
                    <div>
                      <span className="text-white">{b.entity_name}</span>
                      <span className="text-text-secondary"> · {b.role} · {b.status}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Bankruptcy (PACER) */}
          {result?.bankruptcy && result.bankruptcy.status !== "none" && (
            <>
              <SectionHeader>Bankruptcy</SectionHeader>
              <div className={cn(
                "rounded-[8px] p-3 space-y-1",
                result.bankruptcy.status === "active" ? "bg-danger/10 border border-danger/40" : "bg-warning/10 border border-warning/40"
              )}>
                <div className="flex items-center gap-2 text-[13px] font-semibold">
                  <ShieldAlert size={14} className={result.bankruptcy.status === "active" ? "text-danger" : "text-warning"} />
                  <span className={result.bankruptcy.status === "active" ? "text-danger" : "text-warning"}>
                    Chapter {result.bankruptcy.chapter} · {result.bankruptcy.status.toUpperCase()}
                  </span>
                </div>
                <KV label="Case" value={result.bankruptcy.case_number} mono />
                <KV label="Court" value={result.bankruptcy.court} />
                <KV label="Filed" value={shortDate(result.bankruptcy.filed_date)} mono />
                {result.bankruptcy.automatic_stay_active && (
                  <div className="text-danger text-[11px] font-bold tracking-[0.06em] mt-1">
                    AUTOMATIC STAY IN EFFECT · 11 U.S.C. § 362
                  </div>
                )}
              </div>
            </>
          )}

          {/* Civil Cases */}
          {(result?.civil_cases?.length || 0) > 0 && (
            <>
              <SectionHeader>Civil Cases</SectionHeader>
              <ul className="space-y-1.5 text-[12px]">
                {result.civil_cases.slice(0, 4).map((c, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-white truncate">{c.caption}</div>
                      <div className="text-text-secondary text-[11px]">
                        <span className="jea-mono">{c.case_number}</span> · {c.court} · <span className="jea-date">{shortDate(c.filed_date)}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-text-secondary capitalize">{c.status}</div>
                      {c.disposition && <div className="text-text-muted capitalize text-[11px]">{c.disposition}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Federal Cases */}
          {(result?.federal_cases?.length || 0) > 0 && (
            <>
              <SectionHeader>Federal Cases</SectionHeader>
              <ul className="space-y-1.5 text-[12px]">
                {result.federal_cases.slice(0, 3).map((c, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-white truncate">{c.caption}</div>
                      <div className="text-text-secondary text-[11px]">
                        <span className="jea-mono">{c.case_number}</span> · {c.court}
                      </div>
                    </div>
                    <div className="text-right shrink-0 text-text-secondary capitalize">{c.status}</div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Docket Events */}
          {(result?.docket_events?.length || 0) > 0 && (
            <>
              <SectionHeader>Recent Docket Events</SectionHeader>
              <ul className="space-y-1 text-[12px]">
                {result.docket_events.slice(0, 5).map((e, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="jea-date text-[11px] w-20 shrink-0">{shortDate(e.date)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-white">{e.description}</div>
                      <div className="text-text-secondary text-[11px]">
                        {e.event_type} · <span className="jea-mono">{e.case_number}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Alerts */}
          {(result?.alerts?.length || 0) > 0 && (
            <div className="mt-4 space-y-2">
              {result.alerts.map((a, i) => <AlertRow key={i} a={a} />)}
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default BrokerResultCard;
